import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { api } from '../lib/api'
import toast from 'react-hot-toast'
import { 
  Plus, 
  Trash2, 
  Smartphone, 
  CheckCircle, 
  XCircle,
  Clock,
  AlertCircle,
  Settings
} from 'lucide-react'

interface NFCDaemon {
  GeraeteID: string
  Lehrer: string | null
  Status: string
  CreatedAt: string
  LastSeen: string
  ts: string
}

export default function DeviceManager() {
  const [deviceCode, setDeviceCode] = useState('')
  const [isRegistering, setIsRegistering] = useState(false)
  const queryClient = useQueryClient()

  // Fetch user's NFC daemons
  const { data: daemons = [], isLoading, refetch } = useQuery(
    'nfc-daemons',
    () => api.get('/nfc-daemons').then(res => res.data.data),
    {
      retry: 3,
      onError: (error: any) => {
        toast.error('Fehler beim Laden der Geräte: ' + (error.response?.data?.message || error.message))
      }
    }
  )

  // Register device mutation
  const registerMutation = useMutation(
    (code: string) => api.post('/nfc-daemons/register', { deviceCode: code }),
    {
      onSuccess: (response) => {
        toast.success(response.data.message || 'Gerät erfolgreich registriert')
        setDeviceCode('')
        queryClient.invalidateQueries('nfc-daemons')
      },
      onError: (error: any) => {
        const message = error.response?.data?.message || 'Fehler beim Registrieren des Geräts'
        toast.error(message)
      }
    }
  )

  // Update device status mutation
  const updateStatusMutation = useMutation(
    ({ deviceId, status }: { deviceId: string; status: string }) => 
      api.patch(`/nfc-daemons/${deviceId}/status`, { status }),
    {
      onSuccess: (response) => {
        toast.success(response.data.message || 'Gerätestatus erfolgreich aktualisiert')
        queryClient.invalidateQueries('nfc-daemons')
      },
      onError: (error: any) => {
        const message = error.response?.data?.message || 'Fehler beim Aktualisieren des Gerätestatus'
        toast.error(message)
      }
    }
  )

  // Unregister device mutation
  const unregisterMutation = useMutation(
    (deviceId: string) => api.delete(`/nfc-daemons/${deviceId}`),
    {
      onSuccess: (response) => {
        toast.success(response.data.message || 'Gerät erfolgreich abgemeldet')
        queryClient.invalidateQueries('nfc-daemons')
      },
      onError: (error: any) => {
        const message = error.response?.data?.message || 'Fehler beim Abmelden des Geräts'
        toast.error(message)
      }
    }
  )

  const handleRegister = async () => {
    if (!deviceCode.trim()) {
      toast.error('Bitte geben Sie einen Gerätecode ein')
      return
    }

    if (!/^[0-9A-Z]{6}$/.test(deviceCode.toUpperCase())) {
      toast.error('Der Code muss genau 6 Zeichen lang sein (0-9, A-Z)')
      return
    }

    setIsRegistering(true)
    try {
      await registerMutation.mutateAsync(deviceCode.toUpperCase())
    } finally {
      setIsRegistering(false)
    }
  }

  const handleStatusChange = async (deviceId: string, newStatus: string) => {
    await updateStatusMutation.mutateAsync({ deviceId, status: newStatus })
  }

  const handleUnregister = async (deviceId: string) => {
    if (window.confirm('Möchten Sie dieses Gerät wirklich abmelden?')) {
      await unregisterMutation.mutateAsync(deviceId)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'inactive':
        return <XCircle className="h-5 w-5 text-red-500" />
      case 'offline':
        return <XCircle className="h-5 w-5 text-gray-500" />
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-500" />
      default:
        return <AlertCircle className="h-5 w-5 text-gray-500" />
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Aktiv'
      case 'inactive':
        return 'Inaktiv'
      case 'offline':
        return 'Offline'
      case 'pending':
        return 'Ausstehend'
      default:
        return 'Unbekannt'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900">Gerätemanager</h1>
        <p className="mt-1 text-sm text-gray-500">
          Registrieren und verwalten Sie Ihre NFC-Geräte
        </p>
      </div>

      {/* Device Registration */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Neues Gerät registrieren
        </h2>
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <label htmlFor="deviceCode" className="block text-sm font-medium text-gray-700 mb-2">
              Gerätecode (6 Zeichen)
            </label>
            <input
              type="text"
              id="deviceCode"
              value={deviceCode}
              onChange={(e) => setDeviceCode(e.target.value.toUpperCase())}
              placeholder="z.B. ABC123"
              maxLength={6}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Geben Sie den 6-stelligen Code ein, der auf dem Gerät angezeigt wird
            </p>
          </div>
          <button
            onClick={handleRegister}
            disabled={isRegistering || !deviceCode.trim()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRegistering ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Registriere...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                Registrieren
              </>
            )}
          </button>
        </div>
      </div>

      {/* Device List */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Meine registrierten Geräte
          </h2>
        </div>
        
        {isLoading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">Lade Geräte...</p>
          </div>
        ) : daemons.length === 0 ? (
          <div className="p-6 text-center">
            <Smartphone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-sm font-medium text-gray-900 mb-2">Keine Geräte registriert</h3>
            <p className="text-sm text-gray-500">
              Registrieren Sie Ihr erstes NFC-Gerät mit dem Code oben.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Geräte-ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registriert am
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Letzte Aktivität
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {daemons.map((daemon: NFCDaemon) => (
                  <tr key={daemon.GeraeteID} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Smartphone className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900 font-mono">
                          {daemon.GeraeteID}
                        </span>
                      </div>
                    </td>
                                         <td className="px-6 py-4 whitespace-nowrap">
                       <div className="flex items-center">
                         {getStatusIcon(daemon.Status)}
                         <select
                           value={daemon.Status}
                           onChange={(e) => handleStatusChange(daemon.GeraeteID, e.target.value)}
                           disabled={updateStatusMutation.isLoading}
                           className="ml-2 text-sm text-gray-900 bg-transparent border-none focus:ring-0 focus:outline-none cursor-pointer disabled:opacity-50"
                         >
                           <option value="active">Aktiv</option>
                           <option value="inactive">Inaktiv</option>
                           <option value="offline">Offline</option>
                           <option value="pending">Ausstehend</option>
                         </select>
                       </div>
                     </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(daemon.CreatedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(daemon.LastSeen)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleUnregister(daemon.GeraeteID)}
                        disabled={unregisterMutation.isLoading}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Gerät abmelden"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
